import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Mic, 
  MicOff, 
  Loader2, 
  Upload, 
  FileAudio, 
  Copy, 
  Check, 
  Download,
  Clock,
  Languages,
  Sparkles,
  Volume2,
  Pause,
  Play,
  X,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { cn } from "../lib/utils";
import { useLanguage } from "../context/LanguageContext";

export default function SpeechToTextPage() {
  const { t, isRTL } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [copied, setCopied] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [history, setHistory] = useState([]);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);

  // Load history on mount
  useEffect(() => {
    const saved = localStorage.getItem('speech_history');
    if (saved) {
      setHistory(JSON.parse(saved).slice(0, 10));
    }
  }, []);

  // Save to history
  const saveToHistory = (text, source) => {
    const entry = {
      id: Date.now(),
      text: text.slice(0, 200),
      fullText: text,
      source,
      date: new Date().toISOString()
    };
    const newHistory = [entry, ...history].slice(0, 10);
    setHistory(newHistory);
    localStorage.setItem('speech_history', JSON.stringify(newHistory));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      // Try to use better codec if available
      let options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'audio/webm' };
      }
      
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        clearInterval(timerRef.current);
        setRecordingTime(0);
        await transcribeAudio(audioBlob, 'microphone');
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success(isRTL ? "بدأ التسجيل..." : "Recording started...");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error(isRTL ? "لا يمكن الوصول للميكروفون" : "Cannot access microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob, source = 'file') => {
    setLoading(true);
    const startTime = Date.now();
    
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await api.post(`/speech/transcribe`, formData);
      
      const endTime = Date.now();
      const processingTime = ((endTime - startTime) / 1000).toFixed(1);

      if (response.data.text) {
        setTranscription(response.data.text);
        saveToHistory(response.data.text, source);
        toast.success(
          isRTL 
            ? `تم التحويل في ${processingTime} ثانية` 
            : `Transcribed in ${processingTime}s`
        );
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(isRTL ? "خطأ في التحويل" : "Transcription error");
    } finally {
      setLoading(false);
      setUploadedFile(null);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/webm', 'audio/ogg', 'audio/m4a', 'audio/mp4', 'video/mp4'];
    if (!validTypes.some(type => file.type.includes(type.split('/')[1]))) {
      toast.error(isRTL ? "نوع الملف غير مدعوم" : "Unsupported file type");
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error(isRTL ? "حجم الملف يتجاوز 25MB" : "File size exceeds 25MB");
      return;
    }

    setUploadedFile(file);
    await transcribeAudio(file, 'upload');
    e.target.value = '';
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transcription);
      setCopied(true);
      toast.success(isRTL ? "تم النسخ" : "Copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(isRTL ? "خطأ في النسخ" : "Copy failed");
    }
  };

  const downloadAsText = () => {
    const blob = new Blob([transcription], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcription-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(isRTL ? "جاري التحميل..." : "Downloading...");
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-6" data-testid="speech-to-text-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 pt-16 md:pt-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-foreground flex items-center justify-center">
                <Mic className="w-4 h-4 text-background" />
              </div>
              <div>
                <h1 className="text-base font-bold text-foreground">
                  {isRTL ? "تحويل الصوت لنص" : "Speech to Text"}
                </h1>
                <p className="text-[10px] text-muted-foreground">OpenAI Whisper</p>
              </div>
            </div>
            
            {history.length > 0 && (
              <span className="px-2 py-1 rounded-lg bg-muted text-xs text-muted-foreground">
                {history.length} {isRTL ? "سجل" : "records"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Recording Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-2xl bg-card border border-border"
        >
          <div className="text-center">
            {/* Mic Button */}
            <motion.button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={loading}
              whileTap={{ scale: 0.95 }}
              className={cn(
                "relative w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 transition-all",
                isRecording 
                  ? "bg-red-500 text-white" 
                  : "bg-foreground text-background hover:bg-foreground/90",
                loading && "opacity-50 cursor-not-allowed"
              )}
            >
              {loading ? (
                <Loader2 className="w-8 h-8 animate-spin" />
              ) : isRecording ? (
                <>
                  <MicOff className="w-8 h-8" />
                  {/* Pulsing ring */}
                  <span className="absolute inset-0 rounded-full border-4 border-red-500 animate-ping opacity-30" />
                </>
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </motion.button>

            {/* Status Text */}
            <p className="text-foreground font-medium mb-1">
              {loading 
                ? (isRTL ? "جاري التحويل..." : "Transcribing...")
                : isRecording 
                  ? (isRTL ? "جاري التسجيل" : "Recording")
                  : (isRTL ? "اضغط للتسجيل" : "Tap to record")
              }
            </p>

            {/* Recording Timer */}
            {isRecording && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-2xl font-mono text-red-500"
              >
                {formatTime(recordingTime)}
              </motion.p>
            )}

            {/* Supported formats */}
            {!isRecording && !loading && (
              <p className="text-xs text-muted-foreground mt-2">
                {isRTL ? "أو ارفع ملف صوتي" : "Or upload an audio file"}
              </p>
            )}
          </div>

          {/* Upload Button */}
          {!isRecording && !loading && (
            <motion.button
              onClick={() => fileInputRef.current?.click()}
              whileTap={{ scale: 0.98 }}
              className="w-full mt-4 py-3 rounded-xl border-2 border-dashed border-border hover:border-foreground/30 flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm">{isRTL ? "رفع ملف صوتي" : "Upload audio file"}</span>
            </motion.button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.webm,.ogg"
            onChange={handleFileUpload}
            className="hidden"
          />

          {/* Supported Formats */}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {['MP3', 'WAV', 'M4A', 'WebM', 'OGG'].map(format => (
              <span key={format} className="px-2 py-0.5 rounded bg-muted text-[10px] text-muted-foreground">
                {format}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Result Section */}
        <AnimatePresence mode="wait">
          {transcription && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-6 rounded-2xl bg-card border border-border"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-medium text-foreground">
                    {isRTL ? "النص المحوّل" : "Transcription"}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={copyToClipboard}
                    className="h-8 px-2"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={downloadAsText}
                    className="h-8 px-2"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTranscription("")}
                    className="h-8 px-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div 
                className="p-4 rounded-xl bg-muted/50 min-h-[150px] max-h-[400px] overflow-auto"
                dir="auto"
              >
                <p className="text-foreground whitespace-pre-wrap leading-relaxed">
                  {transcription}
                </p>
              </div>

              {/* Stats */}
              <div className="mt-4 flex items-center justify-center gap-4 text-xs text-muted-foreground">
                <span>{transcription.split(/\s+/).length} {isRTL ? "كلمة" : "words"}</span>
                <span>•</span>
                <span>{transcription.length} {isRTL ? "حرف" : "chars"}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* History Section */}
        {history.length > 0 && !transcription && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-3"
          >
            <h3 className="text-sm font-medium text-muted-foreground px-1">
              {isRTL ? "السجل السابق" : "Recent History"}
            </h3>
            {history.slice(0, 5).map((entry) => (
              <motion.button
                key={entry.id}
                onClick={() => setTranscription(entry.fullText)}
                whileTap={{ scale: 0.98 }}
                className="w-full p-4 rounded-xl bg-card border border-border text-right hover:bg-muted/50 transition-colors"
              >
                <p className="text-sm text-foreground line-clamp-2 mb-2" dir="auto">
                  {entry.text}
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    {entry.source === 'microphone' ? <Mic className="w-3 h-3" /> : <FileAudio className="w-3 h-3" />}
                    {entry.source === 'microphone' ? (isRTL ? 'تسجيل' : 'Recording') : (isRTL ? 'ملف' : 'File')}
                  </span>
                  <span>{(() => {
                    const d = new Date(entry.date);
                    return `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
                  })()}</span>
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Features */}
        {!transcription && history.length === 0 && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Languages, title: isRTL ? "تلقائي اللغة" : "Auto Language", desc: isRTL ? "يكتشف اللغة تلقائياً" : "Detects language automatically" },
              { icon: Clock, title: isRTL ? "سريع" : "Fast", desc: isRTL ? "معالجة فورية" : "Instant processing" },
              { icon: Sparkles, title: isRTL ? "دقيق" : "Accurate", desc: isRTL ? "دقة عالية جداً" : "High accuracy" },
              { icon: FileAudio, title: isRTL ? "ملفات" : "Files", desc: isRTL ? "يدعم ملفات متعددة" : "Multiple formats" },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4 rounded-xl bg-card border border-border"
              >
                <feature.icon className="w-5 h-5 text-muted-foreground mb-2" />
                <h4 className="text-sm font-medium text-foreground">{feature.title}</h4>
                <p className="text-xs text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
